<template>
    <div class="campaign-items-editor">
        <v-row class="mb-2">
            <v-col cols="12">
                <v-switch
                    v-model="localIsItemized"
                    color="primary"
                    hide-details
                    class="mt-0"
                    @change="onToggleChange"
                >
                    <template v-slot:label>
                        <span class="itemized-toggle-label">Break down my goal into specific items</span>
                    </template>
                </v-switch>
            </v-col>
        </v-row>

        <v-expand-transition>
            <div v-if="localIsItemized">
                <v-card elevation="0" outlined class="items-container pa-4 mb-3">
                    <p class="section-subtitle mb-4">What are you raising funds for?</p>

                    <!-- Preset Items -->
                    <div
                        v-for="preset in presetItems"
                        :key="preset.category"
                        class="preset-item mb-3"
                        :class="{ 'preset-item--active': preset.enabled }"
                    >
                        <v-row align="center" no-gutters>
                            <v-col cols="7" sm="8" class="d-flex align-center">
                                <v-checkbox
                                    v-model="preset.enabled"
                                    hide-details
                                    class="mt-0 pt-0 preset-checkbox"
                                    :disabled="disabled"
                                    @change="onPresetChange"
                                >
                                    <template v-slot:label>
                                        <div class="d-flex align-center">
                                            <v-icon
                                                :color="preset.enabled ? 'primary' : 'grey'"
                                                class="mr-2"
                                                size="20"
                                            >
                                                {{ preset.icon }}
                                            </v-icon>
                                            <span
                                                class="preset-label"
                                                :class="{ 'preset-label--active': preset.enabled }"
                                            >
                                                {{ preset.label }}
                                            </span>
                                        </div>
                                    </template>
                                </v-checkbox>
                            </v-col>
                            <v-col cols="5" sm="4">
                                <v-text-field
                                    v-model.number="preset.amount"
                                    outlined
                                    dense
                                    hide-details
                                    type="number"
                                    min="1"
                                    max="1000000"
                                    prefix="$"
                                    placeholder="0"
                                    :disabled="disabled || !preset.enabled"
                                    class="amount-input"
                                    @input="onAmountChange"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Custom Items -->
                    <div
                        v-for="(item, index) in customItems"
                        :key="'custom-' + index"
                        class="custom-item mb-3"
                    >
                        <v-row align="center" no-gutters>
                            <v-col cols="7" sm="8" class="pr-2">
                                <v-text-field
                                    v-model="item.name"
                                    outlined
                                    dense
                                    hide-details
                                    placeholder="Custom item name"
                                    :disabled="disabled"
                                    class="custom-name-input"
                                    @input="emitItems"
                                >
                                    <template v-slot:prepend-inner>
                                        <v-icon color="grey" size="20" class="mr-1">mdi-pencil</v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="4" sm="3">
                                <v-text-field
                                    v-model.number="item.amount"
                                    outlined
                                    dense
                                    hide-details
                                    type="number"
                                    min="1"
                                    max="1000000"
                                    prefix="$"
                                    placeholder="0"
                                    :disabled="disabled"
                                    class="amount-input"
                                    @input="emitItems"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="1" class="text-center">
                                <v-btn
                                    icon
                                    x-small
                                    color="grey"
                                    @click="removeCustomItem(index)"
                                    :disabled="disabled"
                                >
                                    <v-icon size="18">mdi-close</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Add Custom Item Button -->
                    <v-btn
                        text
                        small
                        color="primary"
                        @click="addCustomItem"
                        :disabled="disabled || customItems.length >= 5"
                        class="add-custom-btn mt-2"
                    >
                        <v-icon left small>mdi-plus</v-icon>
                        Add custom item
                    </v-btn>

                    <!-- Total -->
                    <v-divider class="my-4"></v-divider>
                    <div class="d-flex justify-end align-center">
                        <span class="total-label mr-2">Total:</span>
                        <span class="total-amount">${{ itemsTotal.toLocaleString() }}</span>
                    </div>

                    <v-alert
                        v-if="totalMismatch && itemsTotal > 0"
                        type="info"
                        dense
                        text
                        class="mt-4 mb-0"
                    >
                        Your campaign goal will be set to ${{ itemsTotal.toLocaleString() }}
                    </v-alert>
                </v-card>
            </div>
        </v-expand-transition>
    </div>
</template>

<script>
const PRESET_ITEMS = [
    { category: 'tuition', label: 'Tuition', icon: 'mdi-school' },
    { category: 'books', label: 'Books & Supplies', icon: 'mdi-book-open-variant' },
    { category: 'rent', label: 'Rent / Housing', icon: 'mdi-home' },
    { category: 'food', label: 'Food & Meals', icon: 'mdi-food' },
    { category: 'gas', label: 'Gas / Transportation', icon: 'mdi-gas-station' },
    { category: 'furniture', label: 'Furniture', icon: 'mdi-sofa' },
]

export default {
    name: 'CampaignItemsEditor',
    props: {
        isItemized: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            default: () => []
        },
        goalAmount: {
            type: Number,
            default: 0
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localIsItemized: this.isItemized,
            presetItems: PRESET_ITEMS.map(p => ({
                ...p,
                enabled: false,
                amount: null
            })),
            customItems: [],
            isEmitting: false
        }
    },
    computed: {
        itemsTotal() {
            let total = 0
            this.presetItems.forEach(p => {
                if (p.enabled && p.amount > 0) {
                    total += p.amount
                }
            })
            this.customItems.forEach(c => {
                if (c.name && c.amount > 0) {
                    total += c.amount
                }
            })
            return total
        },
        totalMismatch() {
            if (!this.localIsItemized || this.itemsTotal === 0) return false
            return this.itemsTotal !== this.goalAmount
        }
    },
    watch: {
        isItemized(val) {
            this.localIsItemized = val
        },
        items: {
            handler(val) {
                if (this.isEmitting) return
                if (val && val.length > 0) {
                    this.initFromItems(val)
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        initFromItems(items) {
            // Reset presets
            this.presetItems.forEach(p => {
                p.enabled = false
                p.amount = null
            })
            this.customItems = []

            items.forEach(item => {
                const presetIndex = this.presetItems.findIndex(p => p.category === item.category)
                if (presetIndex !== -1 && item.category !== 'custom') {
                    this.presetItems[presetIndex].enabled = true
                    this.presetItems[presetIndex].amount = item.goal_amount
                } else {
                    this.customItems.push({
                        id: item.id,
                        name: item.name,
                        amount: item.goal_amount
                    })
                }
            })
        },
        onToggleChange(val) {
            this.$emit('update:isItemized', val)
            this.emitItems()
        },
        onPresetChange() {
            this.emitItems()
        },
        onAmountChange() {
            this.emitItems()
        },
        addCustomItem() {
            this.customItems.push({
                name: '',
                amount: null
            })
        },
        removeCustomItem(index) {
            this.customItems.splice(index, 1)
            this.emitItems()
        },
        emitItems() {
            this.isEmitting = true
            const allItems = []

            // Add enabled presets
            this.presetItems.forEach(p => {
                if (p.enabled && p.amount > 0) {
                    allItems.push({
                        category: p.category,
                        name: p.label,
                        goal_amount: p.amount
                    })
                }
            })

            // Add valid custom items
            this.customItems.forEach(c => {
                if (c.name && c.amount > 0) {
                    allItems.push({
                        id: c.id,
                        category: 'custom',
                        name: c.name,
                        goal_amount: c.amount
                    })
                }
            })

            this.$emit('update:items', allItems)
            this.$emit('itemsChanged', allItems, this.itemsTotal)
            this.$nextTick(() => {
                this.isEmitting = false
            })
        }
    }
}
</script>

<style scoped>
.itemized-toggle-label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.items-container {
    border-radius: 12px !important;
    background: #fafbfc;
}

.section-subtitle {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.preset-item {
    padding: 12px 16px;
    background: white;
    border-radius: 10px;
    border: 2px solid #e9ecef;
    transition: all 0.2s ease;
}

.preset-item--active {
    border-color: #1976d2;
    background: rgba(25, 118, 210, 0.04);
}

.preset-checkbox {
    margin: 0 !important;
}

.preset-label {
    font-size: 14px;
    font-weight: 500;
    color: #666;
    transition: color 0.2s ease;
}

.preset-label--active {
    color: #333;
}

.amount-input {
    max-width: 140px;
}

.amount-input >>> .v-input__slot {
    min-height: 40px !important;
}

.amount-input >>> input {
    font-weight: 600;
}

.custom-item {
    padding: 8px 12px 8px 16px;
    background: white;
    border-radius: 10px;
    border: 2px dashed #d0d0d0;
}

.custom-name-input >>> .v-input__slot {
    min-height: 40px !important;
}

.add-custom-btn {
    text-transform: none;
    font-weight: 500;
}

.total-label {
    font-size: 14px;
    color: #666;
}

.total-amount {
    font-size: 20px;
    font-weight: 700;
    color: #1976d2;
}
</style>
